import { getTokenKYC3, updateUser } from '@/api/user';
import { Button, Text } from '@/components/common';
import { sendVerificationCode, verificationCodeAndUpdatePhone, verificationPhoneCode } from '@/libs/firebase';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import authReducer from '@/redux/reducers/authReducer';
import { ModalHeader } from '@/styles/common';
import { ERROR_CODES, UserType } from '@/utils/constant';
import { ModalRenderer } from '@/utils/modalUtils';
import * as FS from '@fullstory/browser';
import { get } from 'lodash';
import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage, VerificationInputStyled } from './styled';

const VerificationPhoneCodeModal: React.FC<any> = ({ phoneNumber, isVerified, closeModal, forceLogin }) => {
  const { t } = useTranslation();
  const [verifyCode, setVerificationCode] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigate = useNavigate();
  const userId = useAppSelector((state: any) => get(state, 'user.userInfo.id'));
  const userType = useAppSelector((state: any) => state.user.userType);
  const dispatch = useAppDispatch();
  const verificationRef = useRef(null);

  const onChange = async (e: any) => {
    setErrorMessage('');
    setVerificationCode(e);
  };

  const onHandleConfirmCode = async () => {
    try {
      if (verifyCode) {
        dispatch({ type: 'SHOW_LOADING' });

        if (window?.verificationId) {
          await verificationCodeAndUpdatePhone(verifyCode);
        }
        if (window?.confirmationResult) {
          await verificationPhoneCode(verifyCode);
        }
        // set user id to global state
        dispatch(authReducer.actions.signUpSuccess(userId));
        const { accessToken, kycId, expiredAt } = await getTokenKYC3(userId);
        localStorage.setItem('kyc_token', accessToken);
        localStorage.setItem('kyc_expired_at', expiredAt);
        localStorage.setItem('kyc_id', kycId);
        ModalRenderer.close('all');
        FS && FS.identify(userId);
        if (isVerified) {
          dispatch({ type: 'SHOW_LOADING' });
          dispatch({
            type: 'UPDATE_STATUS',
            payload: { create_account_passed: true },
          });
          if (forceLogin) {
            const kycId = localStorage.getItem('kyc_id');
            const step = localStorage.getItem('kyc_step');
            const sourceLocation = localStorage.getItem('source_location');
            const source = sourceLocation
              ? sourceLocation
              : localStorage.getItem('isMembership') === 'true'
              ? 'membership'
              : userType === UserType.RENTAL_CARS
              ? 'rentalcars'
              : userType === UserType.CONCIERGE_PORTAL
              ? 'concierge-portal'
              : userType === UserType.ADDITIONAL_DRIVER
              ? 'additional-driver'
              : '';
            window.location.href = `/kyc/${kycId}` + (step ? `/${step}` : '') + (source ? `?source=${source}` : '');
            return;
          }
          navigate('/welcome');
        } else {
          dispatch({ type: 'SHOW_LOADING' });
          await updateUser(userId, { phone_number: phoneNumber });
          dispatch({
            type: 'UPDATE_STATUS',
            payload: { create_account_passed: true },
          });
          if (forceLogin) {
            const kycId = localStorage.getItem('kyc_id');
            const step = localStorage.getItem('kyc_step');
            const sourceLocation = localStorage.getItem('source_location');
            const source = sourceLocation
              ? sourceLocation
              : localStorage.getItem('isMembership') === 'true'
              ? 'membership'
              : userType === UserType.RENTAL_CARS
              ? 'rentalcars'
              : userType === UserType.CONCIERGE_PORTAL
              ? 'concierge-portal'
              : userType === UserType.ADDITIONAL_DRIVER
              ? 'additional-driver'
              : '';
            window.location.href = `/kyc/${kycId}` + (step ? `/${step}` : '') + (source ? `?source=${source}` : '');
            return;
          }
          navigate('/welcome');
        }
      }
    } catch (error) {
      dispatch({ type: 'HIDE_LOADING' });
      switch (error) {
        case ERROR_CODES.CODE_EXPIRED:
          setErrorMessage(t('ERROR_MESSAGE_CODE_EXPIRED'));
          return;
        case ERROR_CODES.INVALID_CODE:
          setErrorMessage(t('ERROR_MESSAGE_INVALID_CODE'));
          return;
        default:
          setErrorMessage(t('ERROR_MESSAGE_SOMETHING_WENT_WRONG'));
          break;
      }
    }
  };

  const sendAgain = async () => {
    try {
      // clearRecapchaContent();
      setErrorMessage('');
      setVerificationCode('');
      (verificationRef?.current as any)?.__clearvalues__();
      dispatch({ type: 'SHOW_LOADING' });
      await sendVerificationCode(phoneNumber);
    } catch (error) {
      if (error === ERROR_CODES.INVALID_PHONE_NUMBER || error === ERROR_CODES.EXISTING_PHONE_NUMBER) {
        setErrorMessage(t('ERROR_MESSAGE_INVALID_PHONE_NUMBER'));
        return;
      }
      if (error === ERROR_CODES.TOO_MANY_REQUESTS || error === ERROR_CODES.TOO_MANY_REQUEST_CODE) {
        setErrorMessage(t('ERROR_MESSAGE_TOO_MANY_REQUESTS'));
        return;
      }
      if (error === ERROR_CODES.TOO_MANY_ATTEMPS_CODE) {
        setErrorMessage(t('ERROR_MESSAGE_TOO_MANY_ATTEMPS_CODE'));
        return;
      }
      setErrorMessage(t('ERROR_MESSAGE_SOMETHING_WENT_WRONG'));
    } finally {
      dispatch({ type: 'HIDE_LOADING' });
    }
  };
  const onClose = () => {
    closeModal();
  };
  const isDisableBtn = !!errorMessage || verifyCode?.length !== 6;

  return (
    <>
      <ModalHeader color="primary" variant="title" size="26" sx={{ lineHeight: '28px' }}>
        <img
          style={{ width: '32px', height: '32px', cursor: 'pointer' }}
          src="/images/icon/icon_back.svg"
          onClick={onClose}
          alt=""
        />
        {t('VERIFICATION_CODE')}
      </ModalHeader>
      <Text color="primary" variant="content" size="16" style={{ marginBottom: '24px' }}>
        <Trans
          i18nKey="VERIFICATION_CODE_CONTENT"
          values={{ digit: phoneNumber?.slice(-3) }}
          components={{
            digit: <span className="fs-mask" />,
          }}
        />
      </Text>
      <VerificationInputStyled
        autoFocus
        fields={6}
        onChange={(e: any) => onChange(e)}
        type={'number'}
        className="verif-code"
        placeholder={['0', '0', '0', '0', '0', '0']}
        ref={verificationRef}
      />
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <Text style={{ marginTop: '24px' }} variant="content" color="primary" size="15">
        <Trans
          i18nKey="NOT_RECEIVE_SEND_CODE_AGAIN"
          components={{
            link1: <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => sendAgain()} />,
          }}
        />
      </Text>
      <Button
        sx={{ marginTop: '24px', textTransform: 'none' }}
        fullWidth
        variant="contained"
        color="dark"
        onClick={onHandleConfirmCode}
        disabled={isDisableBtn}
      >
        {t('CONFIRM_CODE')}
      </Button>
    </>
  );
};

export default VerificationPhoneCodeModal;
